/**
 * Brand Icons
 * 
 * Tenant-specific branded icon components.
 * Provides DI for tenant-aware icons without conditional logic in components.
 */

import type { ReactNode } from 'react';
import type { TenantId } from 'tenants/types/tenant';
import NoImageIcon from 'components/icon/noimage-icon';
import NoImageExpIcon from 'components/icon/noimageexp-icon';

/**
 * Brand Icon Registry
 * 
 * Maps tenant IDs to their branded icon components.
 * Centralized configuration for tenant-specific branding.
 */
const brandIconRegistry = {
  noImage: {
    zoocasa: NoImageIcon,
    expRealtyCa: NoImageExpIcon,
    expRealtyUs: NoImageExpIcon,
    expCommercialUs: NoImageExpIcon,
    expCommercialCa: NoImageExpIcon,
    expInService: NoImageExpIcon,
  },
} as const;

type BrandIconType = keyof typeof brandIconRegistry;

/**
 * Get Brand Icon Component
 * 
 * Returns the appropriate branded icon component for a tenant.
 * Provides dependency injection for tenant-specific icons.
 * 
 * @param iconType - Type of brand icon (e.g., 'noImage')
 * @param tenantId - Tenant identifier
 * @returns Icon component for the tenant
 * 
 * @example
 * const NoImageIconComponent = getBrandIcon('noImage', tenantId);
 * return <NoImageIconComponent />;
 */
export function getBrandIcon(
  iconType: BrandIconType,
  tenantId: TenantId
): () => ReactNode {
  return brandIconRegistry[iconType][tenantId];
}

/**
 * Brand Icon Types
 * 
 * Available brand icon types for type-safe access.
 */
export const brandIconTypes = {
  NO_IMAGE: 'noImage' as const,
} as const;
